import { useCallback, useEffect, useRef, useState } from 'react';
import { buildKakaoAuthorizeUrl } from '../../oauth/kakao';
import {
  loginWithAppleIdentity,
  loginWithEmail,
  resetEmailPassword,
  sendEmailVerificationCode,
  signupWithEmail,
  verifyEmailCode,
} from '../../api/auth/authApi';
import { setAuthTokens } from '../../auth/tokenStorage';
import useAutoClearMessage from '../../hooks/useAutoClearMessage';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

const TERMS_URL = process.env.REACT_APP_TERMS_URL || '/terms';
const PRIVACY_URL = process.env.REACT_APP_PRIVACY_URL || '/privacy';
const MARKETING_CONSENT_URL =
  process.env.REACT_APP_MARKETING_CONSENT_URL || '/marketing-consent';
const APP_CONTEXT_MESSAGE_TYPE = 'AHSHOOT_APP_CONTEXT';
const APPLE_LOGIN_REQUEST_TYPE = 'AHSHOOT_APPLE_LOGIN_REQUEST';
const APPLE_LOGIN_RESULT_TYPE = 'AHSHOOT_APPLE_LOGIN_RESULT';
const PENDING_SOCIAL_SIGNUP_KEY = 'pendingSocialSignup';
const PENDING_SOCIAL_CONSENT_KEY = 'pendingSocialConsent';

function parseBridgePayload(rawPayload) {
  if (!rawPayload) {
    return null;
  }

  if (typeof rawPayload === 'string') {
    try {
      return JSON.parse(rawPayload);
    } catch (error) {
      return null;
    }
  }

  if (typeof rawPayload === 'object') {
    return rawPayload;
  }

  return null;
}

function isReactNativeWebViewAvailable() {
  return Boolean(
    window.ReactNativeWebView &&
      typeof window.ReactNativeWebView.postMessage === 'function',
  );
}

function isAppleLoginCanceled(errorCode) {
  const normalizedCode = String(errorCode || '')
    .trim()
    .toUpperCase();

  return (
    normalizedCode === 'CANCEL' ||
    normalizedCode === 'CANCELED' ||
    normalizedCode === 'CANCELLED' ||
    normalizedCode === 'USER_CANCELED' ||
    normalizedCode === 'USER_CANCELLED'
  );
}

function extractResponseMessage(responseData, fallbackMessage) {
  if (!responseData || typeof responseData !== 'object') {
    return fallbackMessage;
  }

  return (
    responseData.resultMessage ||
    responseData.message ||
    responseData.errorMessage ||
    responseData.error?.message ||
    fallbackMessage
  );
}

function isSuccessfulApiResult(responseData) {
  if (!responseData || typeof responseData !== 'object') {
    return true;
  }

  if (typeof responseData.success === 'boolean') {
    return responseData.success;
  }

  if (responseData.resultCode != null) {
    const normalizedCode = String(responseData.resultCode).trim().toUpperCase();
    return normalizedCode === '200' || normalizedCode === 'OK' || normalizedCode === 'SUCCESS';
  }

  if (responseData.code != null) {
    const normalizedCode = String(responseData.code).trim().toUpperCase();
    return normalizedCode === '200' || normalizedCode === 'OK' || normalizedCode === 'SUCCESS';
  }

  return true;
}

function isVerifiedCodeResponse(responseData) {
  if (!responseData || typeof responseData !== 'object') {
    return false;
  }

  if (typeof responseData.verified === 'boolean') {
    return responseData.verified;
  }
  if (typeof responseData.data?.verified === 'boolean') {
    return responseData.data.verified;
  }
  if (typeof responseData.isVerified === 'boolean') {
    return responseData.isVerified;
  }
  if (typeof responseData.data?.isVerified === 'boolean') {
    return responseData.data.isVerified;
  }

  // 명시 필드가 없는 스펙은 success/resultCode 기반 판정만 사용한다.
  return true;
}

function resolveTokenPair(responseData) {
  const normalizedResponse =
    responseData && typeof responseData === 'object' ? responseData : {};
  const nestedData =
    normalizedResponse?.data && typeof normalizedResponse.data === 'object'
      ? normalizedResponse.data
      : {};

  const accessToken =
    normalizedResponse.accessToken ||
    normalizedResponse.access_token ||
    nestedData.accessToken ||
    nestedData.access_token ||
    '';
  const refreshToken =
    normalizedResponse.refreshToken ||
    normalizedResponse.refresh_token ||
    nestedData.refreshToken ||
    nestedData.refresh_token ||
    '';

  return { accessToken, refreshToken };
}

function sanitizeSocialConsentErrorMessage(error) {
  const rawMessage = String(error?.message || '').trim();
  if (
    error?.status === 400 &&
    (rawMessage.includes('필수 동의') ||
      rawMessage.includes('약관') ||
      rawMessage.includes('개인정보'))
  ) {
    return '이용약관 및 개인정보 처리방침 동의 후 다시 시도해 주세요.';
  }
  return rawMessage || '소셜 로그인 처리 중 오류가 발생했습니다.';
}

function getResponseField(responseData, fieldName) {
  if (!responseData || typeof responseData !== 'object') {
    return null;
  }
  if (responseData[fieldName] !== undefined) {
    return responseData[fieldName];
  }
  if (
    responseData.data &&
    typeof responseData.data === 'object' &&
    responseData.data[fieldName] !== undefined
  ) {
    return responseData.data[fieldName];
  }
  return null;
}

function hasResponseField(responseData, fieldName) {
  if (!responseData || typeof responseData !== 'object') {
    return false;
  }

  if (responseData[fieldName] !== undefined) {
    return true;
  }

  return Boolean(
    responseData.data &&
      typeof responseData.data === 'object' &&
      responseData.data[fieldName] !== undefined,
  );
}

function getResponseBoolean(responseData, fieldName, fallback = false) {
  const value = getResponseField(responseData, fieldName);
  return typeof value === 'boolean' ? value : fallback;
}

function ClientLoginPage() {
  const [authMode, setAuthMode] = useState('login');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [agreedMarketing, setAgreedMarketing] = useState(false);
  const [isEmailCodeSent, setIsEmailCodeSent] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationFeedback, setVerificationFeedback] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [socialErrorMessage, setSocialErrorMessage] = useState('');
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [isResetSendingCode, setIsResetSendingCode] = useState(false);
  const [isResetSubmitting, setIsResetSubmitting] = useState(false);
  const [resetFeedback, setResetFeedback] = useState('');
  const [resetError, setResetError] = useState('');
  const [appContext, setAppContext] = useState({
    platform: '',
    supportsAppleLogin: false,
  });
  const [isAppleLoginSubmitting, setIsAppleLoginSubmitting] = useState(false);
  const appleLoginInFlightRef = useRef(false);
  const appleCancelMessageShownRef = useRef(false);
  const appleConsentSnapshotRef = useRef({
    agreedTerms: false,
    agreedPrivacy: false,
    agreedMarketing: false,
  });
  useAutoClearMessage(message, setMessage);
  useAutoClearMessage(errorMessage, setErrorMessage);
  useAutoClearMessage(socialErrorMessage, setSocialErrorMessage);
  useAutoClearMessage(verificationFeedback, setVerificationFeedback);
  useAutoClearMessage(verificationError, setVerificationError);
  useAutoClearMessage(resetFeedback, setResetFeedback);
  useAutoClearMessage(resetError, setResetError);
  useBodyScrollLock(isResetModalOpen);

  const isSignupMode = authMode === 'signup';
  const showAppleLoginButton =
    appContext.platform === 'ios' && appContext.supportsAppleLogin === true;
  const hasRequiredSocialConsent = agreedTerms && agreedPrivacy;

  const resetEmailVerificationState = () => {
    setVerificationCode('');
    setIsEmailCodeSent(false);
    setIsEmailVerified(false);
    setVerifiedEmail('');
    setVerificationFeedback('');
    setVerificationError('');
  };

  const handleEmailChange = (nextEmailValue) => {
    setEmail(nextEmailValue);
    if (!isSignupMode) {
      return;
    }
    setVerificationCode('');
    setIsEmailCodeSent(false);
    setIsEmailVerified(false);
    setVerifiedEmail('');
    setVerificationFeedback('');
    setVerificationError('');
  };

  const handleSendEmailCode = async () => {
    if (isSendingCode || isVerifyingCode || isSubmitting) {
      return;
    }
    const normalizedEmail = String(email || '').trim();
    if (!normalizedEmail) {
      setErrorMessage('이메일을 먼저 입력해 주세요.');
      return;
    }

    setErrorMessage('');
    setMessage('');
    setVerificationError('');
    setVerificationFeedback('');
    setIsSendingCode(true);
    try {
      const responseData = await sendEmailVerificationCode({ email: normalizedEmail });
      if (!isSuccessfulApiResult(responseData)) {
        throw new Error(
          extractResponseMessage(responseData, '인증코드 발송에 실패했습니다.'),
        );
      }

      setIsEmailCodeSent(true);
      setIsEmailVerified(false);
      setVerifiedEmail('');
      setVerificationFeedback('인증코드를 보냈습니다. 메일함을 확인해 주세요.');
    } catch (error) {
      setIsEmailCodeSent(false);
      setIsEmailVerified(false);
      setVerifiedEmail('');
      setVerificationError(error?.message || '인증코드 발송에 실패했습니다.');
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    if (isVerifyingCode || isSendingCode || isSubmitting) {
      return;
    }
    const normalizedEmail = String(email || '').trim();
    const normalizedCode = String(verificationCode || '').trim();
    if (!normalizedEmail) {
      setErrorMessage('이메일을 먼저 입력해 주세요.');
      return;
    }
    if (!normalizedCode) {
      setErrorMessage('인증코드를 입력해 주세요.');
      return;
    }

    setErrorMessage('');
    setMessage('');
    setVerificationError('');
    setVerificationFeedback('');
    setIsVerifyingCode(true);
    try {
      const responseData = await verifyEmailCode({
        email: normalizedEmail,
        code: normalizedCode,
      });
      if (!isSuccessfulApiResult(responseData) || !isVerifiedCodeResponse(responseData)) {
        throw new Error(
          extractResponseMessage(responseData, '인증코드가 올바르지 않습니다. 다시 확인해 주세요.'),
        );
      }

      setIsEmailVerified(true);
      setVerifiedEmail(normalizedEmail);
      setVerificationFeedback('이메일 인증이 완료되었습니다.');
    } catch (error) {
      setIsEmailVerified(false);
      setVerifiedEmail('');
      setVerificationError(
        error?.message || '인증코드 확인에 실패했습니다. 다시 시도해 주세요.',
      );
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const blockSocialLoginWithoutRequiredConsent = () => {
    if (hasRequiredSocialConsent) {
      return false;
    }
    setSocialErrorMessage(
      '이용약관 및 개인정보 처리방침(필수) 동의 후 소셜 로그인을 진행해 주세요.',
    );
    setMessage('');
    return true;
  };

  const handleKakaoLoginStart = () => {
    if (isSubmitting || isAppleLoginSubmitting) {
      return;
    }
    if (blockSocialLoginWithoutRequiredConsent()) {
      return;
    }

    const authorizeUrl = buildKakaoAuthorizeUrl();
    if (!authorizeUrl) {
      setSocialErrorMessage(
        '카카오 OAuth 환경변수가 설정되지 않았습니다. REACT_APP_KAKAO_REST_API_KEY, REACT_APP_KAKAO_REDIRECT_URI를 확인해 주세요.',
      );
      setMessage('');
      return;
    }

    setSocialErrorMessage('');

    sessionStorage.setItem(
      PENDING_SOCIAL_CONSENT_KEY,
      JSON.stringify({
        agreedTerms,
        agreedPrivacy,
        agreedMarketing,
        createdAt: Date.now(),
      }),
    );
    window.location.replace(authorizeUrl);
  };

  const handleEmailAuth = async (event) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }

    const normalizedEmail = String(email || '').trim();
    const normalizedNickname = String(nickname || '').trim();

    if (!normalizedEmail || !password) {
      setErrorMessage('이메일과 비밀번호를 입력해 주세요.');
      return;
    }
    if (isSignupMode) {
      if (!normalizedNickname) {
        setErrorMessage('닉네임을 입력해 주세요.');
        return;
      }
      if (password.length < 8) {
        setErrorMessage('비밀번호는 8자 이상으로 입력해 주세요.');
        return;
      }
      if (password !== confirmPassword) {
        setErrorMessage('비밀번호 확인이 일치하지 않습니다.');
        return;
      }
      if (!isEmailVerified || verifiedEmail !== normalizedEmail) {
        setErrorMessage('이메일 인증을 완료해 주세요.');
        return;
      }
      if (!agreedTerms || !agreedPrivacy) {
        setErrorMessage('이용약관 및 개인정보 처리방침(필수) 동의가 필요합니다.');
        return;
      }
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setSocialErrorMessage('');
    setMessage('');

    try {
      const responseData = isSignupMode
        ? await signupWithEmail({
            email: normalizedEmail,
            password,
            nickname: normalizedNickname,
            agreedTerms,
            agreedPrivacy,
            agreedMarketing,
          })
        : await loginWithEmail({
            email: normalizedEmail,
            password,
          });
      const { accessToken, refreshToken } = resolveTokenPair(responseData);

      if (accessToken && refreshToken) {
        setAuthTokens({ accessToken, refreshToken });
        window.location.replace('/team');
        return;
      }

      if (isSignupMode) {
        setMessage('회원가입이 완료되었습니다. 이메일 로그인으로 접속해 주세요.');
        setAuthMode('login');
        setEmail('');
        setNickname('');
        setPassword('');
        setConfirmPassword('');
        setAgreedTerms(false);
        setAgreedPrivacy(false);
        setAgreedMarketing(false);
        resetEmailVerificationState();
        return;
      }

      throw new Error('로그인 토큰 응답이 올바르지 않습니다.');
    } catch (error) {
      setErrorMessage(error?.message || '요청 처리 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAppleLoginResult = useCallback(async (payload) => {
    if (!appleLoginInFlightRef.current) {
      return;
    }

    appleLoginInFlightRef.current = false;
    setIsAppleLoginSubmitting(false);

    if (payload?.success !== true) {
      if (isAppleLoginCanceled(payload?.errorCode)) {
        if (!appleCancelMessageShownRef.current) {
          appleCancelMessageShownRef.current = true;
          setSocialErrorMessage('Apple 로그인이 취소되었습니다. 다시 시도해 주세요.');
        }
        return;
      }

      setSocialErrorMessage(
        payload?.errorMessage || 'Apple 로그인에 실패했습니다. 다시 시도해 주세요.',
      );
      return;
    }

    const identityToken = String(payload?.identityToken || '').trim();
    if (!identityToken) {
      setSocialErrorMessage('Apple 로그인 토큰을 확인할 수 없습니다.');
      return;
    }

    setSocialErrorMessage('');
    setMessage('');
    setIsSubmitting(true);
    try {
      const pendingConsent = appleConsentSnapshotRef.current;
      const responseData = await loginWithAppleIdentity({
        identityToken,
        nickname: payload?.nickname || null,
        email: payload?.email || null,
        agreedTerms: pendingConsent.agreedTerms,
        agreedPrivacy: pendingConsent.agreedPrivacy,
        agreedMarketing: pendingConsent.agreedMarketing,
      });
      const { accessToken, refreshToken } = resolveTokenPair(responseData);
      if (!accessToken || !refreshToken) {
        throw new Error('Apple 로그인 토큰 응답이 올바르지 않습니다.');
      }

      const resolvedEmail = String(
        payload?.email || getResponseField(responseData, 'email') || '',
      ).trim();
      if (!resolvedEmail) {
        throw new Error(
          'Apple 이메일 공유가 필요합니다. Apple 계정 설정에서 이메일 공유 후 다시 시도해 주세요.',
        );
      }

      const isNewUser = getResponseBoolean(responseData, 'newUser', false);
      const hasAgreedTermsField = hasResponseField(responseData, 'agreedTerms');
      const hasAgreedPrivacyField = hasResponseField(responseData, 'agreedPrivacy');
      const shouldCheckConsentInResponse =
        hasAgreedTermsField && hasAgreedPrivacyField;
      const agreedTerms = getResponseBoolean(responseData, 'agreedTerms', true);
      const agreedPrivacy = getResponseBoolean(responseData, 'agreedPrivacy', true);
      const requiresSignupByConsent =
        shouldCheckConsentInResponse && (!agreedTerms || !agreedPrivacy);

      if (isNewUser || requiresSignupByConsent) {
        sessionStorage.setItem(
          PENDING_SOCIAL_SIGNUP_KEY,
          JSON.stringify({
            provider: 'APPLE',
            userId: getResponseField(responseData, 'userId'),
            email: resolvedEmail,
            nickname:
              payload?.nickname || getResponseField(responseData, 'nickname') || '',
            profileImageUrl:
              getResponseField(responseData, 'profileImageUrl') || null,
            accessToken,
            refreshToken,
          }),
        );
        setMessage('추가 회원가입 정보 입력을 위해 이동합니다.');
        window.setTimeout(() => {
          window.location.replace('/auth/signup');
        }, 500);
        return;
      }

      setAuthTokens({ accessToken, refreshToken });
      window.location.replace('/team');
    } catch (error) {
      setSocialErrorMessage(sanitizeSocialConsentErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  useEffect(() => {
    const handleBridgeMessage = (event) => {
      const payload = parseBridgePayload(event?.data);
      if (!payload || typeof payload !== 'object') {
        return;
      }

      if (payload.type === APP_CONTEXT_MESSAGE_TYPE) {
        setAppContext({
          platform: String(payload.platform || '').trim().toLowerCase(),
          supportsAppleLogin: payload.supportsAppleLogin === true,
        });
        return;
      }

      if (payload.type === APPLE_LOGIN_RESULT_TYPE) {
        handleAppleLoginResult(payload);
      }
    };

    window.addEventListener('message', handleBridgeMessage);
    document.addEventListener('message', handleBridgeMessage);
    return () => {
      window.removeEventListener('message', handleBridgeMessage);
      document.removeEventListener('message', handleBridgeMessage);
    };
  }, [handleAppleLoginResult]);

  const handleAppleLoginRequest = () => {
    if (isSubmitting || isAppleLoginSubmitting) {
      return;
    }
    if (blockSocialLoginWithoutRequiredConsent()) {
      return;
    }
    if (!isReactNativeWebViewAvailable()) {
      setSocialErrorMessage('앱 환경에서만 Apple 로그인을 사용할 수 있습니다.');
      return;
    }

    appleConsentSnapshotRef.current = {
      agreedTerms,
      agreedPrivacy,
      agreedMarketing,
    };
    appleCancelMessageShownRef.current = false;
    appleLoginInFlightRef.current = true;
    setIsAppleLoginSubmitting(true);
    setSocialErrorMessage('');
    setMessage('');

    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: APPLE_LOGIN_REQUEST_TYPE,
      }),
    );
  };

  const openResetModal = () => {
    setIsResetModalOpen(true);
    setResetEmail(String(email || '').trim());
    setResetCode('');
    setResetPassword('');
    setResetPasswordConfirm('');
    setResetFeedback('');
    setResetError('');
  };

  const closeResetModal = () => {
    if (isResetSendingCode || isResetSubmitting) {
      return;
    }
    setIsResetModalOpen(false);
    setResetCode('');
    setResetPassword('');
    setResetPasswordConfirm('');
    setResetFeedback('');
    setResetError('');
  };

  const handleSendResetCode = async () => {
    if (isResetSendingCode || isResetSubmitting) {
      return;
    }
    const normalizedEmail = String(resetEmail || '').trim();
    if (!normalizedEmail) {
      setResetError('이메일을 입력해 주세요.');
      return;
    }

    setResetError('');
    setResetFeedback('');
    setIsResetSendingCode(true);
    try {
      const responseData = await sendEmailVerificationCode({ email: normalizedEmail });
      if (!isSuccessfulApiResult(responseData)) {
        throw new Error(
          extractResponseMessage(responseData, '인증코드 발송에 실패했습니다.'),
        );
      }
      setResetFeedback('비밀번호 재설정용 인증코드를 보냈습니다.');
    } catch (error) {
      setResetError(error?.message || '인증코드 발송에 실패했습니다.');
    } finally {
      setIsResetSendingCode(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    if (isResetSubmitting || isResetSendingCode) {
      return;
    }

    const normalizedEmail = String(resetEmail || '').trim();
    const normalizedCode = String(resetCode || '').trim();
    if (!normalizedEmail || !normalizedCode || !resetPassword) {
      setResetError('이메일, 인증코드, 새 비밀번호를 입력해 주세요.');
      return;
    }
    if (resetPassword.length < 8) {
      setResetError('새 비밀번호는 8자 이상으로 입력해 주세요.');
      return;
    }
    if (resetPassword !== resetPasswordConfirm) {
      setResetError('비밀번호 확인이 일치하지 않습니다.');
      return;
    }

    setResetError('');
    setResetFeedback('');
    setIsResetSubmitting(true);
    try {
      const responseData = await resetEmailPassword({
        email: normalizedEmail,
        code: normalizedCode,
        newPassword: resetPassword,
      });
      if (!isSuccessfulApiResult(responseData)) {
        throw new Error(
          extractResponseMessage(responseData, '비밀번호 재설정에 실패했습니다.'),
        );
      }

      setResetFeedback('비밀번호가 변경되었습니다. 새 비밀번호로 로그인해 주세요.');
      setPassword('');
      setTimeout(() => {
        setIsResetModalOpen(false);
      }, 700);
    } catch (error) {
      setResetError(error?.message || '비밀번호 재설정에 실패했습니다.');
    } finally {
      setIsResetSubmitting(false);
    }
  };

  return (
    <main
      className="grid min-h-screen place-items-center p-6"
      style={{
        background:
          'radial-gradient(circle at 18% 20%, #e8ffeb 0, transparent 36%), radial-gradient(circle at 80% 0, #dff1ff 0, transparent 34%), linear-gradient(165deg, #f8fafc 0%, #eef2ff 100%)',
      }}
    >
      <section className="w-full max-w-[460px] rounded-[20px] border border-[#dde4f0] bg-white/85 px-7 pb-[30px] pt-[34px] shadow-[0_16px_40px_rgba(16,24,40,0.09)] backdrop-blur-[6px]">
        <p className="mb-[14px] inline-block rounded-full bg-[#111827] px-[10px] py-1 text-xs font-bold tracking-[0.08em] text-white">
          AH SHOOT
        </p>
        <h1 className="m-0 text-[clamp(28px,4.5vw,34px)] leading-[1.2] tracking-[-0.03em]">
          축구팀 운영을 더 스마트하게
        </h1>
        <p className="mt-[14px] text-[15px] leading-[1.65] text-[#475467]">
          팀 일정, 출석, 공지, 경기 기록까지 한 곳에서 관리하세요. 카카오 계정으로
          빠르게 시작할 수 있습니다.
        </p>

        <section className="mt-[24px] rounded-2xl border border-[#eaecf0] bg-[#f8fafc] p-3">
          <p className="m-0 text-xs font-semibold text-[#344054]">
            약관 동의 (소셜 로그인/이메일 회원가입 공통)
          </p>
          <p className="mt-1 text-[11px] text-[#667085]">
            필수 항목 동의 후 카카오/Apple 로그인과 이메일 회원가입을 진행할 수 있습니다.
          </p>
          <div className="mt-2 grid gap-2">
            <label className="flex items-start gap-2 rounded-lg border border-[#eaecf0] bg-white px-3 py-2 text-xs text-[#475467]">
              <input
                type="checkbox"
                checked={agreedTerms}
                onChange={(event) => setAgreedTerms(event.target.checked)}
                className="mt-[2px] h-4 w-4 rounded border-[#d0d5dd]"
              />
              <span>
                [필수]{' '}
                <a
                  href={TERMS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-[#344054] underline"
                >
                  이용약관
                </a>
                에 동의합니다.
              </span>
            </label>

            <label className="flex items-start gap-2 rounded-lg border border-[#eaecf0] bg-white px-3 py-2 text-xs text-[#475467]">
              <input
                type="checkbox"
                checked={agreedPrivacy}
                onChange={(event) => setAgreedPrivacy(event.target.checked)}
                className="mt-[2px] h-4 w-4 rounded border-[#d0d5dd]"
              />
              <span>
                [필수]{' '}
                <a
                  href={PRIVACY_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-[#344054] underline"
                >
                  개인정보 처리방침
                </a>
                에 동의합니다.
              </span>
            </label>

            <label className="flex items-start gap-2 rounded-lg border border-[#eaecf0] bg-white px-3 py-2 text-xs text-[#475467]">
              <input
                type="checkbox"
                checked={agreedMarketing}
                onChange={(event) => setAgreedMarketing(event.target.checked)}
                className="mt-[2px] h-4 w-4 rounded border-[#d0d5dd]"
              />
              <span>
                [선택]{' '}
                <a
                  href={MARKETING_CONSENT_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-[#344054] underline"
                >
                  마케팅 정보 수신 정책
                </a>
                에 동의합니다.
              </span>
            </label>
          </div>
        </section>

        <div className="mt-[14px]">
          <button
            type="button"
            onClick={handleKakaoLoginStart}
            disabled={isSubmitting || isAppleLoginSubmitting}
            className="flex h-[52px] w-full items-center justify-center gap-2.5 rounded-xl border-0 bg-[#fee500] text-base font-bold text-[#191919] transition duration-100 hover:-translate-y-px hover:brightness-[0.98] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span
              className="h-6 w-6 rounded-full bg-[#191919] text-center text-xs font-extrabold leading-6 text-[#fee500]"
              aria-hidden="true"
            >
              K
            </span>
            카카오로 시작하기
          </button>
        </div>

        {showAppleLoginButton && (
          <div className="mt-3">
            <button
              type="button"
              onClick={handleAppleLoginRequest}
              disabled={isSubmitting || isAppleLoginSubmitting}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-[#111827] bg-[#111827] px-4 text-sm font-semibold text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span aria-hidden="true" className="text-base leading-none">
                
              </span>
              {isAppleLoginSubmitting ? 'Apple 로그인 요청 중...' : 'Apple로 로그인'}
            </button>
          </div>
        )}

        {socialErrorMessage && (
          <p className="mt-3 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]">
            {socialErrorMessage}
          </p>
        )}

        <div className="my-5 flex items-center gap-3">
          <div className="h-px flex-1 bg-[#d0d5dd]" />
          <span className="text-xs font-semibold text-[#667085]">또는 이메일로</span>
          <div className="h-px flex-1 bg-[#d0d5dd]" />
        </div>

        <section className="rounded-2xl border border-[#eaecf0] bg-white p-4">
          <div className="mb-4 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => {
                setAuthMode('login');
                setErrorMessage('');
                setSocialErrorMessage('');
                setMessage('');
                setEmail('');
                setPassword('');
                setNickname('');
                setConfirmPassword('');
                resetEmailVerificationState();
              }}
              className={`h-10 rounded-lg text-sm font-semibold ${
                !isSignupMode
                  ? 'bg-[#111827] text-white'
                  : 'border border-[#d0d5dd] bg-white text-[#344054]'
              }`}
            >
              이메일 로그인
            </button>
            <button
              type="button"
              onClick={() => {
                setAuthMode('signup');
                setErrorMessage('');
                setSocialErrorMessage('');
                setMessage('');
                setPassword('');
                setConfirmPassword('');
                setNickname('');
                resetEmailVerificationState();
              }}
              className={`h-10 rounded-lg text-sm font-semibold ${
                isSignupMode
                  ? 'bg-[#111827] text-white'
                  : 'border border-[#d0d5dd] bg-white text-[#344054]'
              }`}
            >
              이메일 회원가입
            </button>
          </div>

          {isSignupMode && (!agreedTerms || !agreedPrivacy) && (
            <p className="mb-3 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-xs text-[#b42318]">
              회원가입을 위해 상단 약관 동의(필수)를 먼저 체크해 주세요.
            </p>
          )}

          <form className="grid gap-3" onSubmit={handleEmailAuth}>
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#344054]" htmlFor="email-auth-email">
                이메일
              </label>
              <input
                id="email-auth-email"
                type="email"
                value={email}
                onChange={(event) => handleEmailChange(event.target.value)}
                className="h-11 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                placeholder="you@example.com"
                autoComplete={isSignupMode ? 'email' : 'username'}
              />
            </div>

            {isSignupMode && (
              <div>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <label className="block text-xs font-semibold text-[#344054]" htmlFor="email-auth-code">
                    이메일 인증코드
                  </label>
                  <button
                    type="button"
                    onClick={handleSendEmailCode}
                    disabled={isSendingCode || isVerifyingCode || isSubmitting}
                    className="h-7 rounded-md border border-[#d0d5dd] bg-white px-2 text-[11px] font-semibold text-[#344054] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSendingCode ? '발송 중...' : isEmailCodeSent ? '재발송' : '인증코드 발송'}
                  </button>
                </div>
                <div className="flex gap-2">
                  <input
                    id="email-auth-code"
                    type="text"
                    value={verificationCode}
                    onChange={(event) => setVerificationCode(event.target.value)}
                    className={`h-11 w-full rounded-lg border px-3 text-sm outline-none ${
                      verificationError
                        ? 'border-[#fda29b] bg-[#fef3f2] focus:border-[#f97066]'
                        : 'border-[#d0d5dd] focus:border-[#667085]'
                    }`}
                    placeholder="메일로 받은 인증코드 입력"
                    maxLength={12}
                  />
                  <button
                    type="button"
                    onClick={handleVerifyEmailCode}
                    disabled={
                      isVerifyingCode ||
                      isSendingCode ||
                      isSubmitting ||
                      !String(email || '').trim()
                    }
                    className={`h-11 shrink-0 rounded-lg px-3 text-sm font-semibold ${
                      isEmailVerified
                        ? 'border border-[#a6f4c5] bg-[#ecfdf3] text-[#067647]'
                        : 'border border-[#d0d5dd] bg-white text-[#344054]'
                    } disabled:cursor-not-allowed disabled:opacity-60`}
                  >
                    {isVerifyingCode ? '확인 중...' : isEmailVerified ? '인증 완료' : '코드 확인'}
                  </button>
                </div>

                {verificationFeedback && (
                  <p className="mt-1 text-[11px] font-semibold text-[#067647]">
                    {verificationFeedback}
                  </p>
                )}
                {verificationError && (
                  <p className="mt-1 text-[11px] font-semibold text-[#b42318]">
                    {verificationError}
                  </p>
                )}
                {!verificationFeedback && !verificationError && (
                  <p className="mt-1 text-[11px] text-[#667085]">
                    {isEmailVerified && verifiedEmail === String(email || '').trim()
                      ? '현재 이메일 인증이 완료되었습니다.'
                      : '인증코드 발송 후 코드 확인을 완료해야 회원가입할 수 있습니다.'}
                  </p>
                )}
              </div>
            )}

            {isSignupMode && (
              <div>
                <label className="mb-1 block text-xs font-semibold text-[#344054]" htmlFor="email-auth-nickname">
                  닉네임
                </label>
                <input
                  id="email-auth-nickname"
                  type="text"
                  value={nickname}
                  onChange={(event) => setNickname(event.target.value)}
                  className="h-11 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                  placeholder="사용할 닉네임"
                  autoComplete="nickname"
                  maxLength={20}
                />
              </div>
            )}

            <div>
              <label className="mb-1 block text-xs font-semibold text-[#344054]" htmlFor="email-auth-password">
                비밀번호
              </label>
              <input
                id="email-auth-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="h-11 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                placeholder={isSignupMode ? '8자 이상 입력' : '비밀번호 입력'}
                autoComplete={isSignupMode ? 'new-password' : 'current-password'}
              />
            </div>

            {isSignupMode && (
              <>
                <div>
                  <label
                    className="mb-1 block text-xs font-semibold text-[#344054]"
                    htmlFor="email-auth-password-confirm"
                  >
                    비밀번호 확인
                  </label>
                  <input
                    id="email-auth-password-confirm"
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    className="h-11 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                    placeholder="비밀번호를 다시 입력"
                    autoComplete="new-password"
                  />
                </div>

              </>
            )}

            {message && (
              <p className="rounded-lg border border-[#abefc6] bg-[#ecfdf3] px-3 py-2 text-sm text-[#067647]">
                {message}
              </p>
            )}
            {errorMessage && (
              <p className="rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]">
                {errorMessage}
              </p>
            )}

            <button
              type="submit"
              disabled={
                isSubmitting ||
                (isSignupMode &&
                  (!agreedTerms ||
                    !agreedPrivacy ||
                    !isEmailVerified ||
                    verifiedEmail !== String(email || '').trim()))
              }
              className="mt-1 h-11 rounded-lg bg-[#111827] text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#98a2b3]"
            >
              {isSubmitting
                ? '처리 중...'
                : isSignupMode
                  ? '이메일로 회원가입'
                  : '이메일로 로그인'}
            </button>

            {!isSignupMode && (
              <button
                type="button"
                onClick={openResetModal}
                className="text-left text-xs font-semibold text-[#667085] underline underline-offset-2"
              >
                비밀번호를 잊으셨나요?
              </button>
            )}
          </form>
        </section>

        <p className="mt-4 text-xs leading-[1.5] text-[#6b7280]">
          카카오/Apple 로그인과 이메일 회원가입은 상단 필수 약관 동의 후 진행됩니다.
        </p>
      </section>

      {isResetModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8">
          <div
            className="absolute inset-0 bg-[#0f172a]/55 backdrop-blur-[1px]"
            onClick={closeResetModal}
            aria-hidden="true"
          />
          <section className="relative z-10 w-full max-w-[520px] rounded-3xl border border-[#dbe4f4] bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,0.3)] md:p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="m-0 text-xs font-semibold tracking-wide text-[#475467]">
                  PASSWORD RESET
                </p>
                <h3 className="mt-1 text-xl font-semibold text-[#0f172a]">비밀번호 재설정</h3>
                <p className="mt-2 text-sm text-[#667085]">
                  인증코드 확인 후 새 비밀번호로 변경할 수 있습니다.
                </p>
              </div>
              <button
                type="button"
                onClick={closeResetModal}
                disabled={isResetSendingCode || isResetSubmitting}
                aria-label="비밀번호 재설정 모달 닫기"
                className="grid h-9 w-9 place-items-center rounded-lg border border-[#d0d5dd] bg-white text-base font-semibold text-[#344054] disabled:cursor-not-allowed disabled:opacity-60"
              >
                X
              </button>
            </div>

            <form className="mt-5 grid gap-3" onSubmit={handleResetPassword}>
              <div>
                <label className="mb-1 block text-xs font-semibold text-[#344054]" htmlFor="reset-email">
                  이메일
                </label>
                <div className="flex gap-2">
                  <input
                    id="reset-email"
                    type="email"
                    value={resetEmail}
                    onChange={(event) => setResetEmail(event.target.value)}
                    className="h-11 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                    placeholder="you@example.com"
                  />
                  <button
                    type="button"
                    onClick={handleSendResetCode}
                    disabled={isResetSendingCode || isResetSubmitting}
                    className="h-11 shrink-0 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isResetSendingCode ? '발송 중...' : '인증코드 발송'}
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-[#344054]" htmlFor="reset-code">
                  인증코드
                </label>
                <input
                  id="reset-code"
                  type="text"
                  value={resetCode}
                  onChange={(event) => setResetCode(event.target.value)}
                  className="h-11 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                  placeholder="메일로 받은 인증코드"
                  maxLength={12}
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-[#344054]" htmlFor="reset-password">
                  새 비밀번호
                </label>
                <input
                  id="reset-password"
                  type="password"
                  value={resetPassword}
                  onChange={(event) => setResetPassword(event.target.value)}
                  className="h-11 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                  placeholder="8자 이상 입력"
                  autoComplete="new-password"
                />
              </div>

              <div>
                <label
                  className="mb-1 block text-xs font-semibold text-[#344054]"
                  htmlFor="reset-password-confirm"
                >
                  새 비밀번호 확인
                </label>
                <input
                  id="reset-password-confirm"
                  type="password"
                  value={resetPasswordConfirm}
                  onChange={(event) => setResetPasswordConfirm(event.target.value)}
                  className="h-11 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                  placeholder="새 비밀번호를 다시 입력"
                  autoComplete="new-password"
                />
              </div>

              {resetFeedback && (
                <p className="rounded-lg border border-[#abefc6] bg-[#ecfdf3] px-3 py-2 text-sm text-[#067647]">
                  {resetFeedback}
                </p>
              )}
              {resetError && (
                <p className="rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]">
                  {resetError}
                </p>
              )}

              <button
                type="submit"
                disabled={isResetSubmitting || isResetSendingCode}
                className="h-11 rounded-lg bg-[#111827] text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#98a2b3]"
              >
                {isResetSubmitting ? '변경 중...' : '비밀번호 재설정'}
              </button>
            </form>
          </section>
        </div>
      )}
    </main>
  );
}

export default ClientLoginPage;
